import { useCallback } from 'react';

import { type Checkout, useShowConsumerCheckout } from '@givebacks/api/store';
import { Alert, ButtonMantine, Stack, notifications } from 'ui';

import { useShopStorage } from '../../hooks/useShopStorage';
import { useCheckoutForm } from './hooks/useCheckoutForm';
import { getCartIdsFromCheckout, useSessionCheckoutForm } from './utils';

type CheckoutButtonProps = {
  checkoutId: string;
  /**
   * Callback when the checkout is successful
   */
  onSuccess: () => void;
};

const CheckoutButton = ({ checkoutId, onSuccess }: CheckoutButtonProps) => {
  const { setForm } = useSessionCheckoutForm();
  const { removeCart } = useShopStorage();

  const { data: cartIds } = useShowConsumerCheckout({
    checkoutId: checkoutId,
    select: getCartIdsFromCheckout,
  });

  const handleSuccess = useCallback(
    (checkout: Checkout) => {
      if (!cartIds) return;
      notifications.show({
        variant: 'success',
        message: 'Checkout successful!',
        /* Important, longer error messages may be coming through, so we want users to have time to read it */
        autoClose: 10 * 1000,
      });
      onSuccess();
      cartIds.forEach((cartId) => {
        removeCart(cartId);
      });

      setForm({
        firstName: checkout.first_name,
        lastName: checkout.last_name,
        email: checkout.email,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only need to recreate callback when success handler or cart removal changes
    [onSuccess, removeCart]
  );

  const { isCheckoutFormLoading, error, checkout } = useCheckoutForm({
    checkoutId,
    onSuccess: handleSuccess,
  });

  return (
    <Stack>
      <Alert.Collapse title={error} type="error" in={!!error} />
      <ButtonMantine
        variant="gradient"
        onClick={() => void checkout()}
        loading={isCheckoutFormLoading}
      >
        Checkout
      </ButtonMantine>
      {/* <ValidationDebugger /> */}
    </Stack>
  );
};
export { CheckoutButton };
